import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDebounce, useStore } from 'hooks'

import LeaguesStore from 'stores/leagues'
import TeamsStore from 'stores/teams'

import LoadingContainer from 'components/LoadingContainer'
import Checkmark from 'icons/Checkmark'
import { observer } from 'mobx-react-lite'
import { THEME } from 'utils'

const TeamSearchAndSelect = ({
  mobileStickySearchBar = false,
  nonMobileStickySearchBar = false,
  onSelectionsUpdate,
  onViewAllStatusChange
}) => {
  const [activeLeague, setActiveLeague] = useState('NFL')
  const [selectedTeams, setSelectedTeams] = useState({ NFL: [], MLB: [], NBA: [], NHL: [], NCAA: [] })
  const [viewAllStatus, setViewAllStatus] = useState(false)
  const [searchKey, setSearchKey] = useState(null)
  const searchEl = useRef(null)
  const searchAnchorRef = useRef(null)

  const leaguesStore = useStore(LeaguesStore)
  const teamsStore = useStore(TeamsStore)

  // meomized lookup of league ids by league initials
  const leagueIdLookup = useMemo(
    () => leaguesStore.items.reduce((current, { initials, id }) => ({ ...current, [initials]: id }), {}),
    [leaguesStore.items]
  )

  // list of all 5 leagues we're interested in for "universal search"
  const leagueIds = useMemo(
    () =>
      leaguesStore.items
        .map(({ initials, id }) => (['NFL', 'MLB', 'NBA', 'NHL', 'NCAA'].includes(initials) ? id : null))
        .filter((val) => !!val),
    [leaguesStore.items]
  )

  // lexicographical ordered list of teams
  const orderedTeams = useMemo(
    // if there is an active search key, don't mess with the order. the search endpoint handles it well
    () =>
      !searchKey
        ? teamsStore.items.slice().sort((team1, team2) => (team1.name < team2.name ? -1 : 1))
        : teamsStore.items,
    [teamsStore.items]
  )

  // debounced search call when there is a search value
  useDebounce(
    () => {
      if (searchKey === null) return
      else if (!searchKey.length)
        teamsStore.find({ league: leagueIdLookup[activeLeague], populate: 'league(name,initials)' })
      else teamsStore.search(searchKey, { league: leagueIds.join(',') })

      const scrollToAnchorPosition = searchAnchorRef?.current?.offsetTop
      window?.scrollTo(0, scrollToAnchorPosition)
    },
    200,
    [searchKey]
  )

  useEffect(() => {
    onSelectionsUpdate?.(selectedTeams)
  }, [selectedTeams])

  useEffect(() => {
    onViewAllStatusChange?.(viewAllStatus)
  }, [viewAllStatus])

  const handleSearchInput = async (e) => {
    e.preventDefault()
    setSearchKey(searchEl.current.value)
  }

  const handleNavSelect = async (leagueInitials) => {
    await teamsStore.find({ league: leagueIdLookup[leagueInitials], populate: 'league(name,initials)' })
    setActiveLeague(leagueInitials)
    setViewAllStatus(false)
    setSearchKey(null)
    searchEl.current.value = null
    const scrollToAnchorPosition = searchAnchorRef?.current?.offsetTop
    window?.scrollTo(0, scrollToAnchorPosition)
  }

  return (
    <>
      <div className='searchAnchor' ref={searchAnchorRef} />
      <div className='searchAndNav'>
        <input
          type='text'
          id='teamSearchBar'
          ref={searchEl}
          className='sportsBook__formField'
          placeholder='Search For Your Team Here'
          onChange={handleSearchInput}
        />
        {!searchKey && (
          <nav className='leagueTabs'>
            <div
              className={`leagueTab${activeLeague === 'NFL' ? '--active' : ''}`}
              onClick={() => handleNavSelect('NFL')}
            >
              NFL
            </div>
            <div
              className={`leagueTab${activeLeague === 'MLB' ? '--active' : ''}`}
              onClick={() => handleNavSelect('MLB')}
            >
              MLB
            </div>
            <div
              className={`leagueTab${activeLeague === 'NBA' ? '--active' : ''}`}
              onClick={() => handleNavSelect('NBA')}
            >
              NBA
            </div>
            <div
              className={`leagueTab${activeLeague === 'NHL' ? '--active' : ''}`}
              onClick={() => handleNavSelect('NHL')}
            >
              NHL
            </div>
            <div
              className={`leagueTab${activeLeague === 'NCAA' ? '--active' : ''}`}
              onClick={() => handleNavSelect('NCAA')}
            >
              NCAA
            </div>
          </nav>
        )}
      </div>
      <div className='teamResultsContainer'>
        <div className='teamResults'>
          <LoadingContainer isLoading={teamsStore.isLoading} delay={250}>
            {orderedTeams.map(({ name, active, league }, idx) => {
              if (!active) return null
              if (!viewAllStatus && idx > 9) return null
              const teamLeague = typeof league === 'string' ? league : league.initials
              const pickedInItsLeague = selectedTeams[teamLeague]
              const teamIndex = pickedInItsLeague.findIndex((team) => team === name)
              const selected = teamIndex !== -1
              return (
                <div
                  className={`sportsBook__formField teamResult${selected ? '--selected' : ''}`}
                  onClick={() => {
                    if (selected) {
                      // remove it from the form array for the league - toggle effect
                      const arrayWithoutClickedTeam = [...pickedInItsLeague]
                      arrayWithoutClickedTeam.splice(teamIndex, 1)
                      setSelectedTeams({ ...selectedTeams, [teamLeague]: arrayWithoutClickedTeam })
                    } else {
                      // add the team to the form array for the league
                      const arrayWithClickedTeam = [...pickedInItsLeague, name]
                      setSelectedTeams({ ...selectedTeams, [teamLeague]: arrayWithClickedTeam })
                    }
                  }}
                  key={idx}
                >
                  <p className='resultName'>{name}</p>
                  {selected && <Checkmark />}
                </div>
              )
            })}
            {!viewAllStatus && orderedTeams.length > 10 && (
              <div className='viewAll'>
                <button
                  onClick={() => setViewAllStatus(true)}
                  className='sportsBook__submitButton sportsBook__secondaryButton'
                >
                  + View all
                </button>
              </div>
            )}
          </LoadingContainer>
        </div>
      </div>
      <style jsx>{`
        .leagueTabs {
          display: flex;
          justify-content: space-between;
          text-align: center;
          border-bottom: 1px solid #d8d8d8;
          margin-bottom: 1rem;
          max-width: 100vw;
          overflow-x: auto;
        }
        .leagueTab {
          padding: 1rem 0.75rem;
          font-weight: bold;
          cursor: pointer;
        }
        .leagueTab--active {
          padding: 1rem 0.75rem;
          font-weight: bold;
          border-bottom: 3px solid #c62232;
        }
        .teamResult {
          min-height: 44px;
          line-height: 1;
          padding: 15px;
          color: #828181;
          margin: 0 2px 1rem;
          text-align: left;
          cursor: pointer;
          position: relative;
        }
        .teamResult--selected {
          min-height: 44px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          line-height: 1;
          padding: 15px;
          margin: 0 0 1rem;
          text-align: left;
          cursor: pointer;
          position: relative;
          color: #222;
          cursor: pointer;
        }
        .resultName {
          margin: 0 !important;
          padding: 0 !important;
          display: inline-block;
          max-width: 90%;
          letter-spacing: 1.5px;
          line-height: 1;
        }
        .viewAll {
          background: transparent;
        }
        .viewAll button {
          background: transparent;
        }
        .teamResultsContainer {
          position: relative;
          min-height: 656px;
        }
        .searchAndNav {
          position: ${mobileStickySearchBar ? 'sticky' : 'relative'};
          top: ${mobileStickySearchBar ? '0' : 'auto'};
          z-index: ${mobileStickySearchBar ? '5' : 'auto'};
          padding-top: ${mobileStickySearchBar ? '1rem' : '0'};
          background: #fff;
        }
        .searchAnchor {
          visibility: hidden;
          height: 1px;
          width: 0;
        }
        @media (min-width: ${THEME.BREAKPOINTS.SM}px) {
          .searchAndNav {
            position: ${nonMobileStickySearchBar ? 'sticky' : 'relative'};
            top: ${nonMobileStickySearchBar ? '0' : 'auto'};
            z-index: ${nonMobileStickySearchBar ? '5' : 'auto'};
            padding-top: ${nonMobileStickySearchBar ? '1rem' : '0'};
            background: #fff;
          }
        }
      `}</style>
    </>
  )
}

export default observer(TeamSearchAndSelect)
